import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fontStyles } from "components/Common/commonStyles";

// ASTT-7319: generic drill-down dialog, shared by "View Projects"/"View
// Site"/"View DTRs" buttons in ClassRosterDetailsTable.js -- one roster
// can genuinely have multiple Project rows (Parent Client/Client/Program/
// Project), which is exactly why this data was pulled out of the main
// per-roster table row in the first place (it was causing duplicate main
// rows). Site typically has exactly one row, but uses this same
// component for a consistent pattern.
//
// ASTT-7319 follow-up (item 7): onRowAction/rowActionLabel are optional --
// when provided (View DTRs), a leading action-button column renders per
// row, at the beginning per direct request. View Projects/View Site don't
// pass these, so they stay pure read-only tables, unchanged.
export default function ClassRosterDetailDialog({
  open,
  onClose,
  title,
  columns,
  rows = [],
  loading = false,
  onRowAction,
  rowActionLabel = "Select",
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ ...fontStyles, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {title}
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {onRowAction && (
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  )}
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={{ fontWeight: 700 }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    {onRowAction && (
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          onClick={() => onRowAction(row)}
                          sx={{ textTransform: "none" }}
                        >
                          {rowActionLabel}
                        </Button>
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell key={col.id}>{row[col.id] ?? "N/A"}</TableCell>
                    ))}
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + (onRowAction ? 1 : 0)} align="center" sx={{ py: 3, color: "#9e9e9e" }}>
                      No data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

ClassRosterDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array,
  loading: PropTypes.bool,
  onRowAction: PropTypes.func,
  rowActionLabel: PropTypes.string,
};
